import { useId } from "react";
import { useCmsText } from "../../hooks/useCmsText";
import { stripHtml } from "../../utils/html";
import { MAX_MESSAGE } from "../composeMessage";
import type { ComposerApi } from "../useRequestComposer";
import { Glyph } from "./Icons";

const COUNTER_FROM = 400;

/**
 * Passo finale — «Messaggio e recapiti»: la nota (facoltativa, obbligatoria solo
 * per «Altro»), poi nome, email e telefono (obbligatori) e l'azienda (facoltativa).
 *
 * Ordine nel DOM voluto: la nota è l'unica `textarea` e il nome è il primo
 * `input[type=text]` del modulo (nessun campo di testo nei passi precedenti).
 * Nessun attributo `name` sui campi: `form.name`, `form.email`… non devono
 * diventare elementi (HTMLFormElement espone i controlli per nome).
 */
export function ContactStep({ api }: { api: ComposerApi }) {
  const uid = useId();
  const ct = useCmsText("contact");
  const { t, state, steps, errors, composed, authEmail, emailValue, dispatch } = api;
  const d = state.draft;
  const free = !d.intent || d.intent === "other";
  const n = Math.max(1, steps.indexOf(3) + 1);
  const ids = {
    title: `${uid}-t`, note: `${uid}-note`, noteHelp: `${uid}-nh`, noteCount: `${uid}-nc`, noteErr: `${uid}-ne`,
    name: `${uid}-name`, nameErr: `${uid}-nme`, email: `${uid}-email`, emailHelp: `${uid}-eh`, emailErr: `${uid}-ee`,
    company: `${uid}-co`, phone: `${uid}-ph`, phoneHelp: `${uid}-phh`, phoneErr: `${uid}-phe`, summary: `${uid}-sum`,
  };
  const left = MAX_MESSAGE - composed.message.length;
  const showCount = left <= COUNTER_FROM;
  const fromAccount = d.email === null && !!authEmail;
  const failing = (["note", "name", "email", "phone"] as const).filter((k) => errors[k]);
  const fieldLabel = { note: ct("message"), name: ct("name"), email: ct("email"), phone: ct("phone") };
  const optional = t("contact.guide.optional");
  const by = (...list: Array<string | false | null | undefined>) => list.filter(Boolean).join(" ") || undefined;

  return (
    <section data-step="3" hidden={state.step !== 3} aria-labelledby={ids.title} className="rq-section">
      <h3 id={ids.title} ref={api.setEl("h3")} tabIndex={-1} data-rq-focus-target className="rq-step__title">
        <span className="rq-sr">{t("contact.guide.a11y.stepPrefix", { n, total: steps.length })} </span>
        {t(free ? "contact.guide.step3.titleFree" : "contact.guide.step3.title")}
      </h3>
      <p className="rq-step__hint">{t(free ? "contact.guide.step3.hintFree" : "contact.guide.step3.hint")}</p>

      {state.attempted && failing.length >= 2 && (
        <div ref={api.setEl("summary")} tabIndex={-1} data-rq-focus-target className="rq-summary" role="group" aria-labelledby={ids.summary}>
          <p id={ids.summary}>{t("contact.guide.errorSummary")}</p>
          <ul>
            {failing.map((k) => (
              <li key={k}>
                <button type="button" onClick={() => api.focusField(k)}>{fieldLabel[k]}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rq-fields">
        <div className="rq-field">
          <label htmlFor={ids.note} className="rq-label">
            {ct("message")}
            {!free && <span className="rq-label__opt"> {optional}</span>}
          </label>
          <p id={ids.noteHelp} className="rq-help">{t(`contact.guide.note.${d.intent ?? "other"}`)}</p>
          <textarea
            id={ids.note}
            ref={api.setEl("note")}
            className="rq-textarea"
            rows={5}
            value={d.note}
            required={free}
            placeholder={stripHtml(ct("messagePlaceholder"))}
            aria-invalid={errors.note || left < 0 ? true : undefined}
            aria-describedby={by(ids.noteHelp, showCount && left >= 0 && ids.noteCount, (errors.note || left < 0) && ids.noteErr)}
            onChange={(e) => dispatch({ type: "note", value: e.currentTarget.value })}
          />
          {showCount && left >= 0 && <p id={ids.noteCount} className="rq-count">{t("contact.guide.note.counter", { count: left })}</p>}
          {errors.note === "required" && (
            <p id={ids.noteErr} className="rq-err"><Glyph name="alert" size={14} />{ct("errMessage")}</p>
          )}
          {(errors.note === "tooLong" || left < 0) && (
            <p id={ids.noteErr} className="rq-err"><Glyph name="alert" size={14} />{t("contact.guide.note.tooLong", { count: Math.max(1, -left) })}</p>
          )}
        </div>

        <div className="rq-field">
          <label htmlFor={ids.name} className="rq-label">{ct("name")}</label>
          <input
            id={ids.name}
            ref={api.setEl("name")}
            className="rq-input"
            type="text"
            autoComplete="name"
            enterKeyHint="next"
            maxLength={200}
            required
            value={d.name}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={by(errors.name && ids.nameErr)}
            onChange={(e) => api.changeField("name", e.currentTarget.value)}
          />
          {errors.name && <p id={ids.nameErr} className="rq-err"><Glyph name="alert" size={14} />{ct("errName")}</p>}
        </div>

        <div className="rq-field">
          <label htmlFor={ids.email} className="rq-label">{ct("email")}</label>
          {fromAccount && <p id={ids.emailHelp} className="rq-help">{t("contact.guide.hints.emailFromAccount")}</p>}
          <input
            id={ids.email}
            ref={api.setEl("email")}
            className="rq-input"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="next"
            maxLength={254}
            required
            value={emailValue}
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={by(fromAccount && ids.emailHelp, errors.email && ids.emailErr)}
            onChange={(e) => api.changeField("email", e.currentTarget.value)}
            onBlur={api.fieldBlur("email")}
          />
          {errors.email && <p id={ids.emailErr} className="rq-err"><Glyph name="alert" size={14} />{ct("errEmail")}</p>}
        </div>

        <div className="rq-field">
          <label htmlFor={ids.company} className="rq-label">
            {ct("company")}<span className="rq-label__opt"> {optional}</span>
          </label>
          <input
            id={ids.company}
            ref={api.setEl("company")}
            className="rq-input"
            type="text"
            autoComplete="organization"
            enterKeyHint="next"
            maxLength={200}
            value={d.company}
            onChange={(e) => api.changeField("company", e.currentTarget.value)}
          />
        </div>

        <div className="rq-field">
          <label htmlFor={ids.phone} className="rq-label">{ct("phone")}</label>
          <p id={ids.phoneHelp} className="rq-help">{t("contact.guide.hints.phone")}</p>
          <input
            id={ids.phone}
            ref={api.setEl("phone")}
            className="rq-input"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            enterKeyHint="next"
            maxLength={32}
            required
            value={d.phone}
            aria-invalid={errors.phone ? true : undefined}
            aria-describedby={by(ids.phoneHelp, errors.phone && ids.phoneErr)}
            onChange={(e) => api.changeField("phone", e.currentTarget.value)}
            onBlur={api.fieldBlur("phone")}
          />
          {errors.phone && <p id={ids.phoneErr} className="rq-err"><Glyph name="alert" size={14} />{ct("errPhone")}</p>}
        </div>
      </div>
    </section>
  );
}
